import { useCallback, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { Reflect01Icon } from '@/components/icons/Reflect01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useFormContext } from '@/components/react-hook-form';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { SplitDispositionModalResult } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/SplitDispositionModal';
import { ToggleGrowAllByInflationMenuItem } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/contextMenu/ToggleGrowAllByInflationMenuItem';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EntityType } from '@/modules/entities/types/EntityType';
import { COLORS } from '@/styles/tokens/colors';

import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
} from '../BeneficiaryDesignationsForm.types';
import { CopyBeneficiaryDesignationsModal } from './copyBeneficiaryDesignations/CopyBeneficiaryDesignationsModal';
import { CopyBeneficiaryDesignationsResult } from './copyBeneficiaryDesignations/CopyBeneficiaryDesignationsModal.types';
import { RemoveAllBeneficiariesMenuItem } from './RemoveAllBeneficiariesMenuItem';
import { SplitBeneficiaryDesignationsModal } from './splitBeneficiaryDesignations/SplitBeneficiaryDesignationsModal';
import { ToggleBdgResiduaryPaysAllTaxesMenuItem } from './ToggleBdgResiduaryPaysAllTaxesMenuItem';
import { ToggleTransferInKindMenuItem } from './ToggleTransferInKindMenuItem';

interface BeneficiaryDesignationTierSectionContextMenuProps {
  tier: 'primary' | 'contingent';
  entityType: EntityType;
  entityIdOverride?: string;
  /** Offers draft entities in the split modal's recipient list, matching the
   * recipient card and the DP split selector. */
  isOnHypotheticalWaterfall: boolean;
  /** Append split-generated recipients to the tier's field array. */
  onBulkAddRecipients: (
    recipients: DispositiveProvisionsFormRecipient[]
  ) => void;
  /** Replace the tier's recipients (used by copy and remove-all). */
  onReplaceRecipients: (
    recipients: DispositiveProvisionsFormRecipient[]
  ) => void;
}

export function BeneficiaryDesignationTierSectionContextMenu({
  tier,
  entityType,
  entityIdOverride,
  isOnHypotheticalWaterfall,
  onBulkAddRecipients,
  onReplaceRecipients,
}: BeneficiaryDesignationTierSectionContextMenuProps) {
  const canEditHouseholdData = useCanEditDispositiveProvisions();
  const { showFeedback } = useFeedback();
  const { control } = useFormContext<BeneficiaryDesignationsFormShape>();
  const { entityId: contextEntityId } = useEntityDetailsContext();
  const currentEntityId = entityIdOverride ?? contextEntityId;

  // Path prefix consumed by ToggleGrowAllByInflationMenuItem and
  // ToggleTransferInKindMenuItem, which append `.recipients.${index}.<field>`.
  const recipientsBasePath =
    `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}` as const;
  const recipients = useWatch({
    control,
    name: `${recipientsBasePath}.recipients`,
  }) as DispositiveProvisionsFormRecipient[] | undefined;
  const hasRecipients = Boolean(recipients?.length);

  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);

  const handleCopyModalClose = useCallback(
    (result: CopyBeneficiaryDesignationsResult | null) => {
      setIsCopyModalOpen(false);
      if (!result) return;
      onReplaceRecipients(result.recipients);
      showFeedback('Beneficiaries copied', { variant: 'success' });
    },
    [onReplaceRecipients, showFeedback]
  );

  const handleSplitResult = useCallback(
    (result: SplitDispositionModalResult | null) => {
      setIsSplitModalOpen(false);
      if (!result) return;
      onBulkAddRecipients(result.recipients);
    },
    [onBulkAddRecipients]
  );

  // Copy and split both operate on an empty tier, so the menu is shown even
  // with no recipients; the toggle/remove items below self-hide when empty.
  if (!canEditHouseholdData) {
    return null;
  }

  return (
    <>
      {isCopyModalOpen && (
        <CopyBeneficiaryDesignationsModal
          isOpen={isCopyModalOpen}
          onClose={handleCopyModalClose}
          hasRecipients={hasRecipients}
          entityType={entityType}
          tier={tier}
          currentEntityId={currentEntityId}
        />
      )}
      {isSplitModalOpen && (
        <SplitBeneficiaryDesignationsModal
          isOpen={isSplitModalOpen}
          onClose={handleSplitResult}
          entityIdOverride={entityIdOverride}
          isOnHypotheticalWaterfall={isOnHypotheticalWaterfall}
        />
      )}
      <ContextMenuButton
        data-testid={`BeneficiaryDesignations-menu-${tier}`}
        size="xs"
        variant="transparent"
      >
        <MenuItem
          key="copy-beneficiaries"
          icon={<Copy07Icon size={16} color={COLORS.GRAY['500']} />}
          label="Copy beneficiaries from another entity"
          onClick={() => setIsCopyModalOpen(true)}
          muiMenuItemProps={{ divider: false }}
        />
        <MenuItem
          key="split-beneficiaries"
          icon={<Reflect01Icon size={16} color={COLORS.GRAY['500']} />}
          label="Create evenly split beneficiaries"
          onClick={() => setIsSplitModalOpen(true)}
          // Separate the create actions from the per-recipient toggles below,
          // but only when those toggles will render (i.e. there are recipients).
          muiMenuItemProps={{ divider: hasRecipients }}
        />
        {/* Order matches the DP death-section menu: residuary, then transfer
            in kind, then grow by inflation. */}
        <ToggleBdgResiduaryPaysAllTaxesMenuItem tier={tier} />
        <ToggleTransferInKindMenuItem recipientsBasePath={recipientsBasePath} />
        <ToggleGrowAllByInflationMenuItem
          recipients={recipients ?? []}
          recipientsBasePath={recipientsBasePath}
          subject="distribution"
        />
        <RemoveAllBeneficiariesMenuItem
          tier={tier}
          hasRecipients={hasRecipients}
          onRemoveAll={() => onReplaceRecipients([])}
        />
      </ContextMenuButton>
    </>
  );
}
